import { useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'

const useDeleteLocation = ({ storeId, refetch, allowsShipments, deliveryMethods }) => {
  const [showDeleteLocationModal, setShowDeleteLocationModal] = useState(false)
  const [locationToDelete, setLocationToDelete] = useState(null)
  const [associatedDeliveryMethods, setAssociatedDeliveryMethods] = useState([])
  const [associatedShippingMethods, setAssociatedShippingMethods] = useState([])
  const { data: shippingMethods } = useFetch(() => configurationServices.getShippingMethods({ storeId }), {
    doInitialRequest: allowsShipments,
  })
  const { fetchData: editDeliveryMethod, loading: editingDeliveryMethod } = useFetch(
    configurationServices.partialEditDeliveryMethod,
    {
      doInitialRequest: false,
    }
  )
  const { fetchData: editShippingMethod, loading: editingShippingMethod } = useFetch(
    configurationServices.partialEditShippingMethod,
    {
      doInitialRequest: false,
    }
  )
  const { fetchData: deleteLocationRequest, loading: deletingLocation } = useFetch(
    configurationServices.deleteLocation,
    {
      doInitialRequest: false,
    }
  )

  const openDeleteLocationModal = location => {
    setLocationToDelete(location)
    if (location?.delivery_method_ids?.length) {
      setAssociatedDeliveryMethods(deliveryMethods?.filter(dm => location?.delivery_method_ids.includes(dm.id)))
    }

    if (location?.shipping_method_ids?.length) {
      setAssociatedShippingMethods(shippingMethods?.filter(sm => location?.shipping_method_ids.includes(sm.id)))
    }

    setShowDeleteLocationModal(true)
  }

  const closeDeleteLocationModal = () => {
    setShowDeleteLocationModal(false)
    setLocationToDelete(null)
    setAssociatedDeliveryMethods([])
  }

  const changeLocationIdOfDeliveryMethod = (locationId, deliveryMethodId) => {
    setAssociatedDeliveryMethods(
      associatedDeliveryMethods.map(dm => {
        if (dm.id === deliveryMethodId) {
          return { ...dm, new_location_id: locationId }
        }
        return dm
      })
    )
  }

  const changeLocationIdOfShippingMethod = (locationId, shippingMethodId) => {
    setAssociatedShippingMethods(
      associatedShippingMethods.map(sm => {
        if (sm.id === shippingMethodId) {
          return { ...sm, new_location_id: locationId }
        }
        return sm
      })
    )
  }

  const isFormValid = () => {
    return (
      associatedDeliveryMethods.every(dm => dm.new_location_id) &&
      associatedShippingMethods.every(sm => sm.new_location_id)
    )
  }

  const editDeliveryMethods = async () => {
    for (const dm of associatedDeliveryMethods) {
      await editDeliveryMethod({
        storeId,
        deliveryMethodId: dm.id,
        payload: { location_id: dm.new_location_id },
      })
    }
  }

  const editShippingMethods = async () => {
    for (const sm of associatedShippingMethods) {
      await editShippingMethod({
        storeId,
        shippingMethodId: sm.id,
        payload: { location_id: sm.new_location_id },
      })
    }
  }

  const deleteLocation = async () => {
    if (associatedDeliveryMethods?.length) {
      await editDeliveryMethods()
    }

    if (associatedShippingMethods?.length) {
      await editShippingMethods()
    }

    await deleteLocationRequest({ storeId, locationId: locationToDelete.id })

    closeDeleteLocationModal()
    refetch()
  }

  const isDeleteLocationActionDisabled = (locations, location) => {
    if (locations?.length === 1 && location?.delivery_method_ids?.length) return true

    return false
  }

  return {
    showDeleteLocationModal,
    locationToDelete,
    openDeleteLocationModal,
    closeDeleteLocationModal,
    associatedDeliveryMethods,
    associatedShippingMethods,
    changeLocationIdOfDeliveryMethod,
    changeLocationIdOfShippingMethod,
    isFormValid,
    deleteLocation,
    deletingLocation: editingDeliveryMethod || deletingLocation || editingShippingMethod,
    shippingMethods,
    editDeliveryMethods,
    editShippingMethod,
    isDeleteLocationActionDisabled,
  }
}

export default useDeleteLocation
